import React, { useRef } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  LOCKED_SESSION_MINUTES,
  charCountLabel,
  normalizePreferredFormats,
} from '../../util/profileEditFields';
import {
  LISTENING_STYLES,
  LISTENING_STYLE_LABELS,
  normalizeListeningStyles,
} from '../../util/listeningStyles';
import {
  OTHER_TOPIC_ID,
  SESSION_TOPIC_LABELS,
  SESSION_TOPICS,
  SESSION_TOPICS_GROUPED,
  normalizeTopics,
  normalizeTopicsOther,
} from '../../util/sessionTopics';
import { SESSION_FORMATS } from '../../util/sessionPricing';

import NamedLink from '../NamedLink/NamedLink';

import css from './ProfileEditFields.module.css';

const FORMAT_ICON_PATHS = {
  chat:
    'M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7A2.5 2.5 0 0 1 17.5 16H10l-4 3.2V16H6.5A2.5 2.5 0 0 1 4 13.5v-7Z',
  voice: 'M12 3.5a3 3 0 0 0-3 3v5a3 3 0 1 0 6 0v-5a3 3 0 0 0-3-3Zm-6 8a6 6 0 0 0 12 0M12 17.5v3',
  video:
    'M4 8.2A2.2 2.2 0 0 1 6.2 6h8.6A2.2 2.2 0 0 1 17 8.2v7.6A2.2 2.2 0 0 1 14.8 18H6.2A2.2 2.2 0 0 1 4 15.8V8.2Zm13 1.3 3.2-2.1a.8.8 0 0 1 1.3.7v7.8a.8.8 0 0 1-1.3.7L17 14.5',
};

/**
 * Back + H1 + subcopy used on Edit Listening / Edit Talking.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const ProfileEditHeader = props => {
  const { backName, backParams, backId, titleId, ledeId } = props;
  return (
    <header className={css.header}>
      <NamedLink className={css.back} name={backName} params={backParams}>
        <FormattedMessage id={backId} />
      </NamedLink>
      <h1 className={css.title}>
        <FormattedMessage id={titleId} />
      </h1>
      <p className={css.lede}>
        <FormattedMessage id={ledeId} />
      </p>
    </header>
  );
};

/**
 * Circle photo + Change (blue) + Remove (pink) + helper, matching Sam’s mock.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const ProfilePhotoRow = props => {
  const {
    inputId,
    previewUrl,
    preview,
    hasPhoto,
    onPick,
    onRemove,
    uploadInProgress,
    disabled,
    helperId = 'ProfileEditFields.photoHelper',
    labelId = 'ProfileEditFields.photoLabel',
    addPhotoId = 'ProfileEditFields.addPhoto',
    changePhotoId = 'ProfileEditFields.changePhoto',
    accept = 'image/*',
    large = false,
    noPhotoFace = null,
    heartPicker = null,
  } = props;
  const intl = useIntl();
  const inputRef = useRef(null);
  const pickerDisabled = !!(disabled || uploadInProgress);
  const openPicker = event => {
    if (event) {
      event.preventDefault();
    }
    if (!pickerDisabled && inputRef.current) {
      inputRef.current.click();
    }
  };
  const photoInner =
    preview ||
    (previewUrl ? (
      <img src={previewUrl} alt="" />
    ) : (
      noPhotoFace || (
        <span className={css.photoPlaceholder} aria-hidden="true">
          +
        </span>
      )
    ));

  return (
    <div className={css.section} data-testid="profile-photo-row">
      <p className={css.sectionLabel}>
        <FormattedMessage id={labelId} />
      </p>
      <div className={css.photoRow}>
        <div
          className={classNames(css.photo, {
            [css.photoPicker]: !pickerDisabled,
            [css.photoLarge]: large,
          })}
          data-testid="profile-photo-circle"
        >
          {photoInner}
          <input
            ref={inputRef}
            id={inputId}
            className={css.fileInputOverlay}
            type="file"
            accept={accept}
            disabled={pickerDisabled}
            onChange={onPick}
            data-testid="profile-photo-input"
            aria-label={intl.formatMessage({
              id: hasPhoto ? changePhotoId : addPhotoId,
            })}
          />
        </div>
        <div className={css.photoActions}>
          <button
            type="button"
            className={css.changePhoto}
            onClick={openPicker}
            disabled={pickerDisabled}
          >
            <FormattedMessage id={hasPhoto ? changePhotoId : addPhotoId} />
          </button>
          {hasPhoto && typeof onRemove === 'function' ? (
            <button
              type="button"
              className={css.removePhoto}
              onClick={onRemove}
              disabled={disabled || uploadInProgress}
            >
              <FormattedMessage id="ProfileEditFields.removePhoto" />
            </button>
          ) : null}
        </div>
      </div>
      <p className={css.helper}>
        <FormattedMessage id={helperId} />
      </p>
      {!hasPhoto && !preview && !previewUrl && heartPicker ? heartPicker : null}
    </div>
  );
};

/**
 * Right-aligned 12/500 counter.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const CharCount = props => {
  const { value, max, className } = props;
  const count = String(value || '').length;
  return (
    <span
      className={classNames(css.charCount, className)}
      aria-label={`${count} of ${max} characters`}
    >
      {charCountLabel(value, max)}
    </span>
  );
};

/**
 * Existing listening styles as a checkbox grid — not mock-only labels.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
/**
 * Multi-select boxed checkables — pink fill + strong border when selected.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const BoxedCheckableGroup = props => {
  const {
    name,
    idPrefix,
    legendId,
    options,
    labels,
    normalize,
    otherName,
    required = false,
    requiredMessage,
  } = props;
  const intl = useIntl();
  const requiredError =
    requiredMessage || intl.formatMessage({ id: 'TopicPicker.required' });
  const requiredValues = value => (normalize(value).length ? undefined : requiredError);
  return (
    <Field name={name} validate={required ? requiredValues : undefined}>
      {({ input, meta }) => {
        const selected = normalize(input.value);
        const toggle = id => {
          input.onChange(
            selected.includes(id) ? selected.filter(item => item !== id) : [...selected, id]
          );
        };
        const hasOther = otherName && selected.includes(OTHER_TOPIC_ID);
        const showRequiredError = required && selected.length === 0;
        return (
          <fieldset
            className={classNames(css.section, css.sectionCard)}
            data-testid={`${idPrefix}-boxes`}
          >
            <legend className={css.sectionLabel}>
              <FormattedMessage id={legendId} />
            </legend>
            <ul className={css.boxedGrid}>
              {options.map(option => {
                if (option.header) {
                  return (
                    <li key={option.id} style={{ gridColumn: '1 / -1', fontSize: 12, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: '#888', marginTop: 8 }}>
                      <FormattedMessage id={option.messageId} />
                    </li>
                  );
                }
                const checked = selected.includes(option.id);
                const label = labels[option.id] || option.label;
                return (
                  <li key={option.id}>
                    <label
                      className={classNames(css.boxedOption, {
                        [css.boxedOptionSelected]: checked,
                      })}
                    >
                      <input
                        id={`${idPrefix}-${option.id}`}
                        type="checkbox"
                        name={name}
                        value={option.id}
                        checked={checked}
                        onChange={() => toggle(option.id)}
                      />
                      <span className={css.boxedOptionLabel}>{label}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
            {otherName ? (
              <Field name={otherName}>
                {({ input: otherInput }) =>
                  hasOther ? (
                    <div className={css.otherField}>
                      <label htmlFor={`${idPrefix}-other-text`} className={css.otherLabel}>
                        <FormattedMessage id="TopicPicker.otherLabel" />
                      </label>
                      <input
                        id={`${idPrefix}-other-text`}
                        className={css.otherInput}
                        type="text"
                        name={otherName}
                        value={normalizeTopicsOther(otherInput.value)}
                        onChange={event => otherInput.onChange(event.target.value)}
                        onBlur={otherInput.onBlur}
                        maxLength={80}
                        placeholder={intl.formatMessage({ id: 'TopicPicker.otherPlaceholder' })}
                        autoComplete="off"
                      />
                    </div>
                  ) : null
                }
              </Field>
            ) : null}
            {showRequiredError ? (
              <p className={css.requiredHint} role="alert">
                {meta.error || requiredError}
              </p>
            ) : null}
          </fieldset>
        );
      }}
    </Field>
  );
};

/**
 * Existing conversation types as boxed checkables.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const TopicCheckboxes = props => {
  const {
    name = 'topics',
    otherName,
    idPrefix = 'topics',
    legendId = 'ProfileEditFields.listeningTopics',
    required = false,
  } = props;
  return (
    <BoxedCheckableGroup
      name={name}
      otherName={otherName}
      idPrefix={idPrefix}
      legendId={legendId}
      options={SESSION_TOPICS_GROUPED}
      labels={SESSION_TOPIC_LABELS}
      normalize={normalizeTopics}
      required={required}
    />
  );
};

/**
 * Existing listening styles as a boxed checkbox grid — not mock-only labels.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const ListeningStyleCheckboxes = props => {
  const { name = 'listeningStyles', idPrefix = 'listening-style' } = props;
  return (
    <BoxedCheckableGroup
      name={name}
      idPrefix={idPrefix}
      legendId="ProfileEditFields.listeningStyleLegend"
      options={LISTENING_STYLES}
      labels={LISTENING_STYLE_LABELS}
      normalize={normalizeListeningStyles}
    />
  );
};

const FormatIcon = ({ format }) => (
  <svg className={css.formatIcon} viewBox="0 0 24 24" aria-hidden="true">
    <path
      d={FORMAT_ICON_PATHS[format]}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Chat / Voice / Video cards. Pink when selected.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const ConversationFormatCards = props => {
  const { name = 'preferredFormats' } = props;
  const intl = useIntl();
  return (
    <Field name={name}>
      {({ input }) => {
        const selected = normalizePreferredFormats(input.value);
        const toggle = id => {
          input.onChange(
            selected.includes(id) ? selected.filter(item => item !== id) : [...selected, id]
          );
        };
        return (
          <fieldset className={classNames(css.section, css.sectionCard)}>
            <legend className={css.sectionLabel}>
              <FormattedMessage id="ProfileEditFields.formatLegend" />
            </legend>
            <div className={css.formatCards} role="group">
              {SESSION_FORMATS.map(format => {
                const isOn = selected.includes(format);
                return (
                  <button
                    key={format}
                    type="button"
                    className={classNames(css.formatCard, { [css.formatCardSelected]: isOn })}
                    aria-pressed={isOn}
                    onClick={() => toggle(format)}
                  >
                    <FormatIcon format={format} />
                    {intl.formatMessage({ id: `ProfileEditFields.format.${format}` })}
                  </button>
                );
              })}
            </div>
          </fieldset>
        );
      }}
    </Field>
  );
};

/**
 * Preferred session length is locked to 15 minutes. No 10/20/60 picker.
 *
 * @returns {JSX.Element}
 */
export const LockedSessionLength = () => (
  <div className={classNames(css.section, css.sectionCard)}>
    <p className={css.sectionLabel}>
      <FormattedMessage id="ProfileEditFields.sessionLengthLegend" />
    </p>
    <div className={css.lockedLength} data-testid="locked-session-length">
      <FormattedMessage
        id="ProfileEditFields.sessionLengthValue"
        values={{ minutes: LOCKED_SESSION_MINUTES }}
      />
    </div>
    <p className={css.lockedHint}>
      <FormattedMessage
        id="ProfileEditFields.sessionLengthHint"
        values={{ minutes: LOCKED_SESSION_MINUTES }}
      />
    </p>
  </div>
);

export { css as profileEditFieldsCss };
